import React, { useEffect, useState } from "react";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import classnames from "classnames";

import { RichTextInputProps } from "./rich-text-input.props";
import { defaultToolbar } from "./rich-text-input.config";
import { useRichTextInputStyles } from "./rich-text-input.styles";

export const RichTextInput = React.forwardRef((props: RichTextInputProps, ref) => {
  const classes = useRichTextInputStyles();

  const { value } = props;

  const [editorFocus, setEditorFocus] = useState<boolean>(false);
  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());
  const [updated, setUpdated] = useState<boolean>(false);

  useEffect(() => {
    if (!updated) {
      const blocksFromHtml = htmlToDraft(value ?? "");
      const contentState = ContentState.createFromBlockArray(
        blocksFromHtml.contentBlocks,
        blocksFromHtml.entityMap
      );
      setEditorState(EditorState.createWithContent(contentState));
    } else {
      if (value === "" && !editorFocus) {
        setEditorState(EditorState.createEmpty());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const toolbar = () => props.toolbar ?? defaultToolbar;

  const onEditorStateChange = (editorState: EditorState) => {
    setUpdated(true);
    setEditorState(editorState);

    const raw = convertToRaw(editorState.getCurrentContent());

    // Make sure to save empty as actually empty, not "<p></p>"
    if (raw.blocks.length === 1 && raw.blocks[0].text === "") {
      return props.onChange("");
    }

    return props.onChange(draftToHtml(raw));
  };

  const editorClasses = classnames({
    [classes.editor]: true,
    [classes.focus]: editorFocus,
    [classes.readonly]: props.disabled,
  });

  const toolbarClasses = classnames({
    [classes.toolbar]: true,
    [classes.hidden]: props.disabled,
  });

  return (
    <div className="editor">
      <Editor
        editorClassName={editorClasses}
        editorRef={props.editorRef}
        editorState={editorState}
        onBlur={() => setEditorFocus(false)}
        onEditorStateChange={onEditorStateChange}
        onFocus={() => setEditorFocus(true)}
        readOnly={props.disabled}
        spellCheck
        toolbar={toolbar()}
        toolbarClassName={toolbarClasses}
        tabIndex={0}
      />
    </div>
  );
});
